import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatAutocomplete, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatChipInputEvent} from '@angular/material/chips';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {COMMA, ENTER} from '@angular/cdk/keycodes';

@Component({
  selector: 'emm-import-legacy-item-dialog',
  templateUrl: './import-legacy-item-dialog.html',
  styleUrl: './import-legacy-item-dialog.scss',
    standalone: false
})
export class ImportLegacyItemDialog implements OnInit {

    private readonly CONTROL_NAME = 'tagInput';

    readonly separatorKeysCodes: number[] = [ENTER, COMMA];

    form: FormGroup;
    selectedTags: string[] = [];
    rejectedPasteTags: string[] = [];
    existingLegacyItems: string[] = [];
    existingCommonItems: string[] = [];
    filteredLegacyItems$: Observable<string[]>;

    @ViewChild('tagInputRef') tagInputRef: ElementRef<HTMLInputElement>;
    @ViewChild('auto') autocomplete: MatAutocomplete;

    get tagInputControl(): FormControl {
        return this.form.get(this.CONTROL_NAME) as FormControl;
    }

    get tagsNotInManagement(): string[] {
        return this.selectedTags.filter(tag => !this.existingLegacyItems.includes(tag));
    }

    get tagsAlreadyInCommonItems(): string[] {
        return this.selectedTags.filter(tag => this.existingCommonItems.includes(tag));
    }

    get canImport(): boolean {
        return this.selectedTags.length > 0
            && this.tagsNotInManagement.length === 0
            && this.tagsAlreadyInCommonItems.length === 0;
    }

    constructor(
        public dialogRef: MatDialogRef<ImportLegacyItemDialog>,
        @Inject(MAT_DIALOG_DATA) public data: { existingLegacyItems: string[], existingCommonItems: string[] }
    ) {}

    ngOnInit() {
        this.existingLegacyItems = this.data?.existingLegacyItems;
        this.existingCommonItems = this.data?.existingCommonItems;
        this.buildForm();
        this.filteredLegacyItems$ = this.tagInputControl.valueChanges.pipe(
            startWith(''),
            map(value => this.filterLegacyItems(value ?? ''))
        );
    }

    buildForm() {
        this.form = new FormGroup({
            [this.CONTROL_NAME]: new FormControl('')
        });
    }

    private filterLegacyItems(value: string): string[] {
        const current = (value ?? '').trim().toUpperCase();
        return this.existingLegacyItems.filter(tag =>
            tag.toUpperCase().includes(current) && !this.selectedTags.includes(tag)
        );
    }

    addTagFromInput(event: MatChipInputEvent): void {
        if (this.autocomplete?.isOpen) {
            return;
        }
        const value = (event.value ?? '').trim().toUpperCase();
        if (value) {
            this.addTag(value);
        }
        event.chipInput?.clear();
        this.tagInputControl.setValue('');
    }

    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const text = event.clipboardData?.getData('text') ?? '';
        const tags = text.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);

        this.rejectedPasteTags = tags.filter(tag => !this.existingLegacyItems.includes(tag));
        tags.filter(tag => this.existingLegacyItems.includes(tag)).forEach(tag => this.addTag(tag));

        this.tagInputControl.setValue('');
        if (this.tagInputRef) {
            this.tagInputRef.nativeElement.value = '';
        }
    }

    private addTag(tag: string): void {
        if (!this.selectedTags.includes(tag)) {
            this.selectedTags = [...this.selectedTags, tag];
        }
    }

    removeTag(tag: string): void {
        this.selectedTags = this.selectedTags.filter(t => t !== tag);
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        this.addTag(event.option.value);
        if (this.tagInputRef) {
            this.tagInputRef.nativeElement.value = '';
        }
        this.tagInputControl.setValue('');
    }

    close(): void {
        this.dialogRef.close();
    }

    import(): void {
        if (this.canImport) {
            this.dialogRef.close(this.selectedTags);
        }
    }
}
